import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
} from '@chakra-ui/react';
import { DashReportForm } from 'views/dash_report/components';
import { useCarriers, useDashReport } from 'hooks/index';
import type { DashReportFormData } from 'views/dash_report/schemas';

interface LeadDashReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  leadId: number;
  onSuccess?: () => void;
}

const LeadDashReportModal = ({
  isOpen,
  onClose,
  leadId,
  onSuccess,
}: LeadDashReportModalProps) => {
  // Use custom hook for carrier fetching (only when modal is open)
  const { carrierOptions, carriers, isLoadingCarriers } = useCarriers({
    enabled: isOpen,
  });

  // Use custom hook for DASH report generation
  const { generateReport, isSubmitting } = useDashReport({
    leadId,
    onSuccess,
    onClose,
  });

  const handleFormSubmit = async (data: DashReportFormData): Promise<void> => {
    await generateReport(data);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      closeOnOverlayClick={!isSubmitting}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Generate DASH Report</ModalHeader>
        <ModalCloseButton isDisabled={isSubmitting} />

        <ModalBody>
          <Text color="gray.600" mb={4} fontSize="sm">
            Generate a DASH driver report for this lead. The report will be
            attached to the lead and available in the Files section.
          </Text>

          <DashReportForm
            carrierOptions={carrierOptions}
            carriers={carriers}
            isLoadingCarriers={isLoadingCarriers}
            onSubmit={handleFormSubmit}
            submitButtonText={
              isSubmitting ? 'Generating Report...' : 'Generate Report'
            }
            isSubmitting={isSubmitting}
          />
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={handleClose}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LeadDashReportModal;
